import { useState, useEffect, useCallback } from 'react';
import { X, Play, Pause, RefreshCw, Clock, Calendar, Trash2, Plus, Zap, AlertCircle, CheckCircle2 } from 'lucide-react';
import { API_URL } from '../config';

interface Schedule {
  id: string;
  flow_id: string;
  flow_name: string;
  schedule_type: string;
  schedule_description: string;
  enabled: boolean;
  cron_expression?: string;
  interval_minutes?: number;
  rss_urls?: string[];
  poll_interval_minutes?: number;
  created_at?: string;
  last_run_at?: string;
  next_run_at?: string;
  run_count: number;
  error_count: number;
  last_error?: string;
}

interface Execution {
  id: string;
  workflow_name: string;
  trigger_type: string;
  started_at?: string;
  finished_at?: string;
  status: string;
  duration_ms?: number;
  nodes_executed: number;
  error?: string;
}

interface SchedulerPanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentFlowId?: string;
  currentFlowName?: string;
  currentFlowDefinition?: any;
}

export default function SchedulerPanel({
  isOpen,
  onClose,
  currentFlowId,
  currentFlowName,
  currentFlowDefinition,
}: SchedulerPanelProps) {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [activeTab, setActiveTab] = useState<'schedules' | 'executions' | 'create'>('schedules');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state for creating schedule
  const [scheduleType, setScheduleType] = useState<'cron' | 'interval' | 'rss_poll'>('cron');
  const [cronExpression, setCronExpression] = useState('0 7 * * *');
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [rssUrls, setRssUrls] = useState('');
  const [cronValidation, setCronValidation] = useState<{ valid: boolean; next_runs: string[] } | null>(null);

  const loadSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/scheduler/schedules`);
      if (res.ok) {
        const data = await res.json();
        setSchedules(data);
      }
    } catch (e) {
      console.error('Error loading schedules:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadExecutions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/scheduler/executions?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setExecutions(data);
      }
    } catch (e) {
      console.error('Error loading executions:', e);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadSchedules();
      loadExecutions();
    }
  }, [isOpen, loadSchedules, loadExecutions]);

  const validateCron = async (expr: string) => {
    try {
      const res = await fetch(`${API_URL}/scheduler/validate-cron?cron_expression=${encodeURIComponent(expr)}`, {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        setCronValidation(data);
      }
    } catch (e) {
      setCronValidation(null);
    }
  };

  useEffect(() => {
    if (scheduleType === 'cron' && cronExpression) {
      const timer = setTimeout(() => validateCron(cronExpression), 500);
      return () => clearTimeout(timer);
    }
  }, [cronExpression, scheduleType]);

  const createSchedule = async () => {
    if (!currentFlowId || !currentFlowName || !currentFlowDefinition) {
      setError('Nessun flow selezionato. Apri prima un flow da schedulare.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const body: any = {
        flow_id: currentFlowId,
        flow_name: currentFlowName,
        flow_definition: currentFlowDefinition,
        schedule_type: scheduleType,
        enabled: true,
      };

      if (scheduleType === 'cron') {
        body.cron_expression = cronExpression;
      } else if (scheduleType === 'interval') {
        body.interval_minutes = intervalMinutes;
      } else if (scheduleType === 'rss_poll') {
        body.rss_urls = rssUrls.split('\n').filter(u => u.trim());
        body.poll_interval_minutes = intervalMinutes;
      }

      const res = await fetch(`${API_URL}/scheduler/schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        setActiveTab('schedules');
        loadSchedules();
      } else {
        const data = await res.json();
        setError(data.detail || 'Errore creazione schedule');
      }
    } catch (e) {
      setError('Errore di rete');
    } finally {
      setLoading(false);
    }
  };

  const toggleSchedule = async (id: string, enabled: boolean) => {
    try {
      await fetch(`${API_URL}/scheduler/schedules/${id}/toggle?enabled=${enabled}`, {
        method: 'POST',
      });
      loadSchedules();
    } catch (e) {
      console.error('Error toggling schedule:', e);
    }
  };

  const triggerNow = async (id: string) => {
    try {
      await fetch(`${API_URL}/scheduler/schedules/${id}/trigger`, {
        method: 'POST',
      });
      loadExecutions();
    } catch (e) {
      console.error('Error triggering schedule:', e);
    }
  };

  const deleteSchedule = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo schedule?')) return;
    
    try {
      await fetch(`${API_URL}/scheduler/schedules/${id}`, {
        method: 'DELETE',
      });
      loadSchedules();
    } catch (e) {
      console.error('Error deleting schedule:', e);
    }
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('it-IT');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-100';
      case 'error': return 'text-red-600 bg-red-100';
      case 'running': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-[900px] max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <Clock className="w-6 h-6" />
            <h2 className="text-xl font-bold">Scheduler (n8n-style)</h2>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('schedules')}
            className={`px-6 py-3 font-medium ${activeTab === 'schedules' ? 'text-amber-600 border-b-2 border-amber-500' : 'text-gray-500'}`}
          >
            📅 Schedules ({schedules.length})
          </button>
          <button
            onClick={() => setActiveTab('executions')}
            className={`px-6 py-3 font-medium ${activeTab === 'executions' ? 'text-amber-600 border-b-2 border-amber-500' : 'text-gray-500'}`}
          >
            📊 Esecuzioni ({executions.length})
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`px-6 py-3 font-medium ${activeTab === 'create' ? 'text-amber-600 border-b-2 border-amber-500' : 'text-gray-500'}`}
          >
            <Plus className="w-4 h-4 inline mr-1" />
            Nuovo Schedule
          </button>
          <div className="flex-1" />
          <button onClick={() => { loadSchedules(); loadExecutions(); }} className="px-4 text-gray-500 hover:text-amber-600">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* Schedules Tab */}
          {activeTab === 'schedules' && (
            <div className="space-y-4">
              {schedules.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun workflow schedulato</p>
                  <button 
                    onClick={() => setActiveTab('create')}
                    className="mt-4 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                  >
                    Crea il primo schedule
                  </button>
                </div>
              ) : (
                schedules.map(schedule => (
                  <div key={schedule.id} className="border rounded-xl p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-lg">{schedule.flow_name}</h3>
                        <p className="text-sm text-gray-600">{schedule.schedule_description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleSchedule(schedule.id, !schedule.enabled)}
                          className={`p-2 rounded-lg ${schedule.enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          title={schedule.enabled ? 'Disabilita' : 'Abilita'}
                        >
                          {schedule.enabled ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => triggerNow(schedule.id)}
                          className="p-2 rounded-lg bg-amber-100 text-amber-600 hover:bg-amber-200"
                          title="Esegui ora"
                        >
                          <Zap className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteSchedule(schedule.id)}
                          className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-3 grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Tipo:</span>
                        <p className="font-medium">{schedule.schedule_type}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Ultima esecuzione:</span>
                        <p className="font-medium">{formatDate(schedule.last_run_at)}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Prossima esecuzione:</span>
                        <p className="font-medium">{formatDate(schedule.next_run_at)}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Esecuzioni:</span>
                        <p className="font-medium">
                          {schedule.run_count} totali
                          {schedule.error_count > 0 && (
                            <span className="text-red-500 ml-1">({schedule.error_count} errori)</span>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    {schedule.last_error && (
                      <div className="mt-2 p-2 bg-red-50 text-red-700 text-sm rounded">
                        ❌ {schedule.last_error}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Executions Tab */}
          {activeTab === 'executions' && (
            <div className="space-y-3">
              {executions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>Nessuna esecuzione registrata</p>
                </div>
              ) : (
                executions.map(exec => (
                  <div key={exec.id} className="border rounded-lg p-3 flex items-center gap-4">
                    <div className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(exec.status)}`}>
                      {exec.status === 'success' && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                      {exec.status === 'error' && <AlertCircle className="w-3 h-3 inline mr-1" />}
                      {exec.status.toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{exec.workflow_name}</p>
                      <p className="text-xs text-gray-500">
                        {formatDate(exec.started_at)} • {exec.trigger_type} • {exec.nodes_executed} nodi
                        {exec.duration_ms && ` • ${exec.duration_ms}ms`}
                      </p>
                    </div>
                    {exec.error && (
                      <div className="text-xs text-red-500 max-w-xs truncate" title={exec.error}>
                        {exec.error}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Create Tab */}
          {activeTab === 'create' && (
            <div className="max-w-xl mx-auto space-y-6">
              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="font-bold text-amber-800 mb-2">📅 Nuovo Schedule</h3>
                <p className="text-sm text-amber-700">
                  Flow corrente: <strong>{currentFlowName || 'Nessun flow selezionato'}</strong>
                </p>
              </div>

              {/* Schedule Type */}
              <div>
                <label className="block font-medium mb-2">Tipo Schedule</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setScheduleType('cron')}
                    className={`px-4 py-2 rounded-lg border-2 ${scheduleType === 'cron' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}
                  >
                    ⏰ Cron Expression
                  </button>
                  <button
                    onClick={() => setScheduleType('interval')}
                    className={`px-4 py-2 rounded-lg border-2 ${scheduleType === 'interval' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}
                  >
                    🔄 Intervallo
                  </button>
                  <button
                    onClick={() => setScheduleType('rss_poll')}
                    className={`px-4 py-2 rounded-lg border-2 ${scheduleType === 'rss_poll' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}
                  >
                    📰 RSS Polling
                  </button>
                </div>
              </div>

              {/* Cron Expression */}
              {scheduleType === 'cron' && (
                <div>
                  <label className="block font-medium mb-2">Cron Expression</label>
                  <input
                    type="text"
                    value={cronExpression}
                    onChange={e => setCronExpression(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg font-mono"
                    placeholder="0 7 * * *"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formato: minuto ora giorno mese giorno-settimana
                  </p>
                  <div className="mt-2 text-sm">
                    <span className="mr-2">Esempi:</span>
                    <button onClick={() => setCronExpression('0 7 * * *')} className="px-2 py-1 bg-gray-100 rounded mr-1 text-xs">Ogni giorno alle 7:00</button>
                    <button onClick={() => setCronExpression('0 */2 * * *')} className="px-2 py-1 bg-gray-100 rounded mr-1 text-xs">Ogni 2 ore</button>
                    <button onClick={() => setCronExpression('0 9 * * 1')} className="px-2 py-1 bg-gray-100 rounded text-xs">Ogni lunedì alle 9:00</button>
                  </div>
                  {cronValidation && (
                    <div className={`mt-2 p-2 rounded text-sm ${cronValidation.valid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {cronValidation.valid ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 inline mr-1" />
                          Prossime esecuzioni:
                          <ul className="mt-1 list-disc list-inside text-xs">
                            {cronValidation.next_runs.slice(0, 3).map((run, i) => (
                              <li key={i}>{formatDate(run)}</li>
                            ))}
                          </ul>
                        </>
                      ) : (
                        <><AlertCircle className="w-4 h-4 inline mr-1" /> Expression non valida</>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Interval */}
              {scheduleType === 'interval' && (
                <div>
                  <label className="block font-medium mb-2">Intervallo (minuti)</label>
                  <input
                    type="number"
                    value={intervalMinutes}
                    onChange={e => setIntervalMinutes(parseInt(e.target.value) || 60)}
                    min={1}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Il workflow verrà eseguito ogni {intervalMinutes} minuti
                  </p>
                </div>
              )}

              {/* RSS Polling */}
              {scheduleType === 'rss_poll' && (
                <>
                  <div>
                    <label className="block font-medium mb-2">URL Feed RSS (uno per riga)</label>
                    <textarea
                      value={rssUrls}
                      onChange={e => setRssUrls(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-2 border rounded-lg font-mono text-sm"
                      placeholder="https://feeds.example.com/rss.xml"
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-2">Intervallo polling (minuti)</label>
                    <input
                      type="number"
                      value={intervalMinutes}
                      onChange={e => setIntervalMinutes(parseInt(e.target.value) || 60)}
                      min={5}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>
                </>
              )}

              {/* Submit */}
              <button
                onClick={createSchedule}
                disabled={loading || !currentFlowId}
                className="w-full py-3 bg-amber-500 text-white rounded-lg font-bold hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creazione...' : '✅ Crea Schedule'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

